// ── Visit DISPLAY status ──────────────────────────────────────────────────
// The real `visits.status` column is the source of truth for the state machine
// (clock-in, billing, payroll, the bot all key off it). It is NEVER mutated by
// anything here. A visit that is genuinely `pending` but already carries an
// effective start time — because an RN filed a `visit_requests` row proposing a
// time, surfaced by the backend pending-overlay — should READ as "Assigned"
// across every agency/admin/ops surface. This module is the one place that
// derives that display status and the matching left-edge stripe, so the API,
// the agency portal, and the admin hub can never drift apart.
//
// Backend mirror of the bucket SQL lives in
// `packages/api/src/services/visit-pending-overlay.ts`.
// Mirrors the `visit_status` pgEnum in packages/api/src/db/schema.ts.
export const VISIT_LIFECYCLE_STATUSES = [
    "pending",
    "assigned",
    "in_progress",
    "post_processing",
    "completed",
    "under_review",
    "pending_correction",
    "sealed",
    "cancelled"
];
// Statuses at or after clock-in: the visit is happening or already happened, so
// it always reads as committed (green stripe) regardless of the `isConfirmed`
// flag. (Per product: in-progress and completed visits show green.)
const POST_ASSIGN_LIFECYCLE = new Set([
    "in_progress",
    "post_processing",
    "completed",
    "under_review",
    "pending_correction",
    "sealed"
]);
// Derive the DISPLAY status from the real status + whether the visit has an
// effective start time (real `startTime`, OR a pending request's proposed time).
// A genuinely-pending visit with a proposed time reads as "assigned"; every
// other status passes through unchanged.
export function deriveDisplayStatus(status, hasEffectiveStartTime) {
    if (status === "pending" && hasEffectiveStartTime) {
        return "assigned";
    }
    return status;
}
// The left-edge confirmation stripe shown on visit rows + drawers:
//   - "confirmed"   → green  (assigned & patient-confirmed, OR the visit is in
//                             progress / completed / sealed — already underway)
//   - "unconfirmed" → amber  (reads as assigned but the time isn't confirmed yet)
//   - "none"        → no stripe (genuinely pending, or cancelled)
// Invariant the product wants: pending ⇒ no stripe; assigned ⇒ always a stripe.
export function visitStripeState(v) {
    if (POST_ASSIGN_LIFECYCLE.has(v.status)) {
        return "confirmed";
    }
    if (v.status === "cancelled") {
        return "none";
    }
    if (v.displayStatus === "assigned") {
        return v.isConfirmed ? "confirmed" : "unconfirmed";
    }
    return "none";
}
export const VISIT_STRIPE_COLORS = {
    confirmed: "#10B981",
    unconfirmed: "#F59E0B"
};
// ── Filter buckets (agency + admin + ops status filter) ───────────────────
// Each bucket maps the display model to a filterable slice. "assigned"
// deliberately overlaps "awaiting_confirmation" + "confirmed" — selecting it
// returns everything that READS as assigned (including request-only visits).
export const VISIT_DISPLAY_BUCKETS = [
    "pending",
    "awaiting_confirmation",
    "confirmed",
    "assigned",
    "in_progress",
    "completed",
    "cancelled"
];
export const VISIT_DISPLAY_BUCKET_LABELS = {
    pending: "Pending",
    awaiting_confirmation: "Awaiting confirmation",
    confirmed: "Confirmed",
    assigned: "Assigned (all)",
    in_progress: "In progress",
    completed: "Completed",
    cancelled: "Cancelled"
};
export function isVisitDisplayBucket(value) {
    return VISIT_DISPLAY_BUCKETS.includes(value);
}
// Pure predicate mirroring the backend bucket SQL. Kept here so client-side
// filtering and tests stay in lock-step with the server.
export function matchesDisplayBucket(bucket, v) {
    switch (bucket) {
        case "pending":
            return v.displayStatus === "pending";
        case "awaiting_confirmation":
            return v.displayStatus === "assigned" && !v.isConfirmed;
        case "confirmed":
            return v.displayStatus === "assigned" && v.isConfirmed;
        case "assigned":
            return v.displayStatus === "assigned";
        case "in_progress":
            return v.status === "in_progress" || v.status === "post_processing";
        case "completed":
            return (v.status === "completed" ||
                v.status === "under_review" ||
                v.status === "pending_correction" ||
                v.status === "sealed");
        case "cancelled":
            return v.status === "cancelled";
        default:
            return false;
    }
}
//# sourceMappingURL=visit-display-status.js.map